import { useEffect, useRef } from 'react'
import { trackSectionView, trackScrollDepth } from '../../lib/firebase'

// List of sections to track
const TRACKED_SECTIONS = [
    'quem-somos',
    'desafio',
    'solucoes',
    'metodologia',
    'lideres',
    'contato'
]

/**
 * Component that initializes analytics tracking for all main sections
 * Uses a single Intersection Observer for efficiency
 */
export function SectionAnalytics() {
    const entryTimes = useRef<Map<string, number>>(new Map())
    const scrollDepths = useRef<Map<string, Set<number>>>(new Map())

    useEffect(() => {
        if (typeof window === 'undefined') return

        // Initialize scroll depths for each section
        TRACKED_SECTIONS.forEach(id => {
            scrollDepths.current.set(id, new Set())
        })

        // Find all sections
        const sections = TRACKED_SECTIONS.map(id => document.getElementById(id)).filter(Boolean) as HTMLElement[]

        // Single Intersection Observer for all sections
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const sectionId = entry.target.id

                    if (entry.isIntersecting) {
                        // Section entered viewport
                        if (!entryTimes.current.has(sectionId)) {
                            entryTimes.current.set(sectionId, Date.now())
                        }
                    } else {
                        // Section left viewport - calculate time spent
                        const entryTime = entryTimes.current.get(sectionId)
                        if (entryTime) {
                            const timeSpent = (Date.now() - entryTime) / 1000
                            trackSectionView(sectionId, timeSpent)
                            entryTimes.current.delete(sectionId)
                        }
                    }
                })
            },
            {
                threshold: 0.1, // Trigger when 10% visible
                rootMargin: '0px'
            }
        )

        sections.forEach(section => observer.observe(section))

        // Scroll handler for depth tracking
        const handleScroll = () => {
            const windowHeight = window.innerHeight

            sections.forEach(section => {
                const sectionId = section.id
                if (!entryTimes.current.has(sectionId)) return // Not currently viewing

                const rect = section.getBoundingClientRect()

                // Calculate scroll progress through section
                if (rect.top < windowHeight && rect.bottom > 0) {
                    const visibleTop = Math.max(0, -rect.top)
                    const scrollProgress = Math.min(100, (visibleTop / rect.height) * 100)

                    const tracked = scrollDepths.current.get(sectionId) || new Set()
                    const milestones = [25, 50, 75, 100] as const

                    for (const milestone of milestones) {
                        if (scrollProgress >= milestone && !tracked.has(milestone)) {
                            tracked.add(milestone)
                            trackScrollDepth(sectionId, milestone)
                        }
                    }
                }
            })
        }

        window.addEventListener('scroll', handleScroll, { passive: true })

        // Cleanup
        return () => {
            observer.disconnect()
            window.removeEventListener('scroll', handleScroll)

            // Track remaining time for any visible sections
            entryTimes.current.forEach((entryTime, sectionId) => {
                const timeSpent = (Date.now() - entryTime) / 1000
                trackSectionView(sectionId, timeSpent)
            })
        }
    }, [])

    return null // This component doesn't render anything
}
